var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import SingleResponseService from "../../Domain/SingleResponse/SingleResponse.service";
import PagedResponseService from "../../Domain/PagedResponse/PagedResponse.service";
import ParamsService from "./Params.service";
import SingleArrayResponseService from "../../Domain/SingleArrayResponse/SingleArrayResponse.service";
import { getVirtualizedAppName } from "../../View/Hooks/useVirtualizedApp";
// TODO REMOVE -- and use WATERMARK_UTILS directly
export function getXSRF() {
    var _a;
    // TODO this should return null if there is no token
    return (_a = WATERMARK_UTILS.getCSRFToken()) !== null && _a !== void 0 ? _a : "";
}
export default class FetchService {
    /**
     * **Fetch data from an endpoint, process single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoint
     * @param {{[string]: any}} params - params to be passed in a query string to the endpoint.
     * @returns {Promise<T>} the data from the single response object.
     */
    static getSingleResponse(url, params = {}) {
        return (FetchService.get(url, params)
            .catch((response) => {
            throw SingleResponseService.processSingleResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(singleResponse: SingleResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
            .then(SingleResponseService.processSingleResponse));
    }
    /**
     *
     * @param {string} url - the url of the endpoint
     * @param {{[string]: any}} params - params to be passed in a query string to the endpoint
     * @returns {Promise<T>} the data from the single array response
     * */
    static getSingleArrayResponse(url, params = {}) {
        return (FetchService.get(url, params)
            .catch((response) => {
            throw SingleArrayResponseService.processSingleArrayResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(singleArrayResponse: SingleArrayResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
            .then(SingleArrayResponseService.processSingleArrayResponse));
    }
    /**
     * Fetch data wrapped in a PagedResponse using a get request.
     *
     * @param {string} url -  - the url of the endpoint
     * @param {[string]: any} params - params to be passed in a query string to the endpoint
     * @returns {Promise<PagedResult<T>} a paged result object with the data from the paged response.
     */
    static getPagedResponse(url, params = {}) {
        return (FetchService.get(url, params)
            .catch((response) => {
            throw PagedResponseService.processPagedResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(pagedResponse: PagedResponse<T>) => PagedResult<T>' is not assignable to parameter of type '(value: unknown) => PagedResult<unknown> | PromiseLike<PagedResult<unknown>>'.
            .then(PagedResponseService.processPagedResponse));
    }
    /**
     * Fetch data using a get request.
     *
     * @param {string} url - the url of the endpoint
     * @param {{[string]: any}} params - params to be passed in a query string to the endpoint.
     * @returns {T} the data from the single response object.
     */
    static get(url, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const virtualizedAppName = getVirtualizedAppName();
            const search = ParamsService.toQueryString(params);
            const response = yield fetch(url + (search.length > 0 ? "?" + search : ""), {
                credentials: "include",
                headers: Object.assign({ "X-Requested-With": "XMLHttpRequest", Accept: "application/json" }, (virtualizedAppName
                    ? { VirtualizedAppName: virtualizedAppName.toString() }
                    : {})),
                method: "GET",
            });
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * **Post data to an endpoint using a post and process the returned single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoint
     * @param {any} body - the data to send to the endpoint
     * @returns {Promise<T>} A promise which resolves with the data from the single response.
     */
    static postSingleResponse(url, body) {
        return FetchService.post(url, body)
            .catch((response) => {
            throw SingleResponseService.processSingleResponse(response);
        })
            .then(SingleResponseService.processSingleResponse);
    }
    /**
     * **Post data to an endpoint using a post and process the returned paged response**
     *
     * Here's what we mean by "*processing*" a paged response:
     * * Paged response is successful - **returned Promise resolves with data**
     * * Paged response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoing
     * @param {any} body - the data to send to the endpoing
     * @returns {Promise<T>} A promise which resolves with the data from the single response.
     */
    static postPagedResponse(url, body) {
        return FetchService.post(url, body)
            .catch((response) => {
            throw PagedResponseService.processPagedResponse(response);
        })
            .then(PagedResponseService.processPagedResponse);
    }
    /**
     * Post data to an endpoint
     *
     * @param {string} url - the url of the endpoint
     * @param {any} body - data to be passed in the body of the request
     */
    static post(url, body) {
        return __awaiter(this, void 0, void 0, function* () {
            const virtualizedAppName = getVirtualizedAppName();
            const response = yield fetch(url, Object.assign(Object.assign({ credentials: "include", method: "POST" }, (body
                ? {
                    headers: Object.assign(Object.assign({ "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Accept: "application/json" }, (virtualizedAppName
                        ? { VirtualizedAppName: virtualizedAppName.toString() }
                        : {})), { "X-XSRF-TOKEN": getXSRF() }),
                }
                : {
                    headers: {
                        "X-XSRF-TOKEN": getXSRF(),
                    },
                })), { body: JSON.stringify(body) }));
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * Post data to an endpoint
     *
     * @param {string} url - the url of the endpoint
     * @param {any} body - data to be passed in the body of the request
     */
    static put(url, body) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(url, {
                credentials: "include",
                method: "PUT",
                body: JSON.stringify(body),
                headers: {
                    "X-XSRF-TOKEN": getXSRF(),
                },
            });
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * **Put data to a endpoint using a post and process the returned single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoint
     * @param {any} body - the data to send to the endpoint
     * @returns {Promise<T>} A promise which resolves with the data from the single response.
     */
    static putSingleResponse(url, body) {
        return (FetchService.put(url, body)
            .catch((response) => {
            throw SingleResponseService.processSingleResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(singleResponse: SingleResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
            .then(SingleResponseService.processSingleResponse));
    }
    /**
     * Delete data from an endpoint
     *
     * @param {string} url - the url of the endpoint
     * @param {any} params - data to be serialized in the url
     */
    static delete(url, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const search = ParamsService.toQueryString(params);
            const response = yield fetch(url + (search.length > 0 ? "?" + search : ""), {
                credentials: "include",
                method: "DELETE",
                headers: {
                    "X-XSRF-TOKEN": getXSRF(),
                },
            });
            let data;
            try {
                data = yield response.json();
            }
            catch (error) {
                if (console.error) {
                    console.error("Could not parse json from fetch", response);
                }
                throw error;
            }
            if (!response.ok) {
                throw data;
            }
            return data;
        });
    }
    /**
     * **Post data to a endpoint using a post and process the returned single response**
     *
     * Here's what we mean by "*processing*" a single response:
     * * Single response is successful - **returned Promise resolves with data**
     * * Single response is unsuccessful - **returned Promise rejects with message**
     *
     * Note that the promise may reject for other reasons, and you should not assume that
     * it will always reject with the message from the single response.  It may make
     * sense to check if the rejected value is a string before trying to display it.
     *
     * @param {string} url - the url of the endpoint
     * @param {any} params - the data to send to the endpoint
     * @returns {Promise<T>} A promise which resolves with the data from the single response.
     */
    static deleteSingleResponse(url, params) {
        return (FetchService.delete(url, params)
            .catch((response) => {
            throw SingleResponseService.processSingleResponse(response);
        })
            // @ts-expect-error - TS2345 - Argument of type '<T>(singleResponse: SingleResponse<T>) => T' is not assignable to parameter of type '(value: unknown) => unknown'.
            .then(SingleResponseService.processSingleResponse));
    }
}
